/**
 * Secure, synchronous-after-warm JavaScript sandbox for the "javascript" cell
 * module. Wraps quickjs-emscripten (WASM): no network/filesystem/host globals.
 * Same engine in the browser and the Node server. Never throws.
 */
import { getQuickJS, type QuickJSWASMModule } from "quickjs-emscripten";

// Per-cell limits. NOTE: EXEC_BUDGET_MS is a SOFT cap — QuickJS polls the
// interrupt handler on a coarse internal cadence, so a bulk-allocation loop can
// run a little longer (ultimately bounded by MEMORY_LIMIT_BYTES) before it is
// killed. Keeping the memory cap tight makes an allocation bomb OOM quickly
// instead of blocking the synchronous Node event loop. 4 MB is ample for a
// single-row cell transform.
const EXEC_BUDGET_MS = 50;
const MEMORY_LIMIT_BYTES = 4 * 1024 * 1024; // 4 MB heap cap (bounds worst-case CPU on allocation bombs)
const MAX_OUTPUT_CHARS = 100_000;

// U+2028 LINE SEPARATOR and U+2029 PARAGRAPH SEPARATOR must be escaped inside
// JSON strings that will be embedded as JS literals.
const LINE_SEP = String.fromCharCode(0x2028);
const PARA_SEP = String.fromCharCode(0x2029);

let engine: QuickJSWASMModule | null = null;
let enginePromise: Promise<QuickJSWASMModule> | null = null;

/** Load the WASM module once. Idempotent and safe to call concurrently. */
export async function warmJsEngine(): Promise<void> {
  if (engine) return;
  if (!enginePromise) enginePromise = getQuickJS();
  engine = await enginePromise;
}

export function isJsEngineReady(): boolean {
  return engine !== null;
}

function coerce(v: unknown): string {
  if (v == null) return "";
  if (typeof v === "string") return v;
  if (typeof v === "number") return Number.isFinite(v) ? String(v) : "";
  if (typeof v === "boolean") return v ? "true" : "false";
  try {
    return JSON.stringify(v) ?? "";
  } catch {
    return "";
  }
}

/** JSON that is also safe to inline as a JS literal (escape line separators). */
function safeJson(value: unknown): string {
  return JSON.stringify(value ?? {})
    .split(LINE_SEP).join("\\u2028")
    .split(PARA_SEP).join("\\u2029");
}

type Scope = { row: Record<string, unknown>; out: Record<string, string> };

function evaluate(code: string, scope: Scope): { value: string; error?: string } {
  if (!engine) {
    void warmJsEngine();
    return { value: "", error: "engine not ready" };
  }
  const wrapped = `(function(){\n"use strict";\nvar row = ${safeJson(scope.row)};\nvar out = ${safeJson(scope.out)};\n${code}\n})()`;

  const ctx = engine.newContext();
  try {
    ctx.runtime.setMemoryLimit(MEMORY_LIMIT_BYTES);
    const deadline = Date.now() + EXEC_BUDGET_MS;
    ctx.runtime.setInterruptHandler(() => Date.now() > deadline);

    const result = ctx.evalCode(wrapped);
    if (result.error) {
      let message = "error";
      try {
        const dumped = ctx.dump(result.error);
        // Surface just the error message for editor previews, not the whole
        // {name, message, stack} object.
        message =
          dumped && typeof dumped === "object" && "message" in dumped
            ? String((dumped as { message?: unknown }).message ?? "error")
            : coerce(dumped);
      } catch {
        /* ignore */
      }
      result.error.dispose();
      return { value: "", error: message };
    }
    let value = "";
    try {
      value = coerce(ctx.dump(result.value));
    } finally {
      result.value.dispose();
    }
    if (value.length > MAX_OUTPUT_CHARS) value = value.slice(0, MAX_OUTPUT_CHARS);
    return { value };
  } catch (err) {
    return { value: "", error: err instanceof Error ? err.message : String(err) };
  } finally {
    ctx.dispose();
  }
}

/** Pure entry point for resolve(): synchronous, never throws, "" on any failure. */
export function runUserJs(code: string, scope: Scope): string {
  try {
    return evaluate(code, scope).value;
  } catch {
    return "";
  }
}

/** Same evaluation, but returns the error message for editor previews. */
export function runUserJsDiagnostic(code: string, scope: Scope): { value: string; error?: string } {
  try {
    return evaluate(code, scope);
  } catch (err) {
    return { value: "", error: err instanceof Error ? err.message : String(err) };
  }
}
